import { API } from '../utils/api'
import {
	ApiMonthAvailabilityResponse,
	ApiShiftsResponse,
	ApiTableLayoutResponse,
	Id,
	ApiProductsResponse,
	ApiVenueDataResponse,
	Journeys,
	ApiClientSecretResponse,
	BookingData,
	ApiResponse,
	VenueId,
} from '../types'
import { log, parseDate } from '../utils/helpers'
import { Booking } from '../utils/booking'

export class RumbaService extends API {
	#venueId: VenueId
	#journey: Journeys | undefined

	constructor(root: string, venueId: VenueId, journey?: Journeys) {
		super(root)
		this.#venueId = venueId
		this.#journey = journey
	}
	/* --------------------------------------------------------
     | RUMBA UTILS
     --------------------------------------------------------- */
	isProd() {
		return 'production' === process.env.REACT_APP_CARDAMOMO_ENV
	}
	get venueId() {
		return this.#venueId
	}
	get journey() {
		return this.#journey
	}
	setJourney(journey: Journeys) {
		this.#journey = journey
	}
	/* --------------------------------------------------------
     | RUMBA ENDPOINTS
     --------------------------------------------------------- */
	async getMonthAvailability(date: Date): ApiMonthAvailabilityResponse {
		log(`rumba -> getMonthAvailability`)
		const month = parseDate("yyyy-mm-dd", date)
		return this.get<ApiMonthAvailabilityResponse>(`${this.#venueId}/availability-by-month/${month.substring(0, 7)}?journey=${this.journey}`)
	}

	async getVenueTableLayout(eventId: Id, locator: string) {
		log(`rumba -> getVenueTableLayout`)

		//['id','name','pax','zone','access','style','x','y','o']
		const response = await this.get<ApiTableLayoutResponse>(`${this.#venueId}/layout?eventId=${eventId}&lite=0&journey=${this.journey}&locator=${locator}`)
		if (response.result !== 'ok') return response

		const tables = response.tables
			.map((table) => {
				let [id, name, pax, zone, access, style, x, y, occupied] = table
				return {
					id,
					name,
					pax,
					zone,
					access,
					style,
					x,
					y,
					occupied,
					side: 'front' as 'front',
					col: 0,
					row: 0,
					score: 0,
				}
			})
			.sort((a, b) => a.name.localeCompare(b.name))

		return { ...response, tables, type: 'complete' as 'complete' }
	}

	async getVenueData() {
		log(`rumba -> getVenueData`)
		return await this.get<ApiVenueDataResponse>(`${this.#venueId}/venue-data`)
	}

	async getShifts(date: Date | string | number) {
		log(`rumba -> getShifts`)
		const iso_date = parseDate('yyyy-mm-dd', date)
		return await this.get<ApiShiftsResponse>(`${this.#venueId}/availability-by-day/${iso_date}?journey=${this.journey}`)
	}

	async getProducts(date: Date | string | number, shiftId: Id, eventId: Id) {
		log(`rumba -> getProducts`)
		const iso_date = parseDate('yyyy-mm-dd hh:mm:ss', date)
		return await this.get<ApiProductsResponse>(`${this.#venueId}/products?date=${iso_date}&shiftId=${shiftId}&eventId=${eventId}&journey=${this.#journey}`)
	}

	async getClientSecret(amount: number, booking: Booking) {
		log(`rumba -> getClientSecret`)

		const iso_date = parseDate('yyyy-mm-dd', booking.date)
		const time = parseDate('hh:mm', booking.date)
		const data = {
			date: iso_date,
			amount,
			locator: booking.locator,
			time,
			language: booking.language,
			name: booking.name,
			email: booking.email,
			phone: booking.phone,
			eventId: booking.eventId,
			tables: booking.tables,
			pax: booking.pax,
			shiftId: booking.shiftId,
			journey: booking.journey,
			products: booking.products,
		}
		return await this.post<ApiClientSecretResponse>(`${this.#venueId}/stripe-intent`, data)
	}

	async saveBooking(bookingData: BookingData & { img?: string; paymentIntentId: string }) {
		log(`rumba -> saveBooking`)
		return await this.post<ApiResponse>(`${this.#venueId}/booking`, bookingData)
	}

	async checkTables(booking: BookingData) {
		log(`rumba -> checkTables`)
		const iso_date = parseDate('yyyy-mm-dd', booking.date)
		const time = parseDate('hh:mm', booking.date)
		const data = {
			date: iso_date,
			locator: booking.locator,
			time,
			name: booking.name,
			email: booking.email,
			phone: booking.phone,
			eventId: booking.eventId,
			tables: booking.tables,
			pax: booking.pax,
			shiftId: booking.shiftId,
			journey: booking.journey,
			products: booking.products,
		}
		return await this.post<ApiResponse>(`${this.venueId}/update-booking`, data)
	}
}
